import { browserSupportsWebAuthn, startRegistration } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { Fingerprint, KeyRound, LoaderCircle, Plus, RefreshCw, ShieldCheck, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import type { PasskeySummary } from "../types";

interface AccessManagerProps {
  open: boolean;
  onClose: () => void;
}

interface RegistrationCeremony {
  ceremonyId: string;
  options: PublicKeyCredentialCreationOptionsJSON;
}

function suggestedLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  if (/Android/i.test(ua)) return "Android 设备";
  if (/Macintosh|Mac OS/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows 设备";
  return "当前设备";
}

function friendlyError(reason: unknown): string {
  if (reason instanceof Error && reason.name === "NotAllowedError") return "注册已取消，设备没有完成指纹、面容或锁屏验证。";
  return reason instanceof Error ? reason.message : "通行密钥操作失败。";
}

function displayDate(value?: string): string {
  if (!value) return "尚未使用";
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function AccessManager({ open, onClose }: AccessManagerProps) {
  const supported = useMemo(() => browserSupportsWebAuthn(), []);
  const [passkeys, setPasskeys] = useState<PasskeySummary[]>([]);
  const [ceremony, setCeremony] = useState<RegistrationCeremony | null>(null);
  const [label, setLabel] = useState(suggestedLabel);
  const [busy, setBusy] = useState<"loading" | "registering" | string | "">("");
  const [error, setError] = useState("");
  const labelRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setBusy("loading");
    setError("");
    try {
      const [list, options] = await Promise.all([
        api.passkeys(),
        supported ? api.passkeyRegistrationOptions() : Promise.resolve(null),
      ]);
      setPasskeys(list.passkeys);
      setCeremony(options);
    } catch (reason) {
      setError(friendlyError(reason));
      setCeremony(null);
    } finally {
      setBusy("");
    }
  }, [supported]);

  useEffect(() => {
    if (!open) return;
    document.body.dataset.dialogOpen = "true";
    void load().then(() => setTimeout(() => labelRef.current?.focus(), 0));
    return () => { delete document.body.dataset.dialogOpen; };
  }, [open, load]);

  if (!open) return null;

  const register = async () => {
    if (!ceremony || !label.trim() || busy) return;
    const active = ceremony;
    setCeremony(null);
    setBusy("registering");
    setError("");
    try {
      const response = await startRegistration({ optionsJSON: active.options });
      await api.verifyPasskeyRegistration(active.ceremonyId, label, response);
      setLabel(suggestedLabel());
      await load();
    } catch (reason) {
      setError(friendlyError(reason));
      setBusy("");
      try {
        setCeremony(await api.passkeyRegistrationOptions());
      } catch {
        setCeremony(null);
      }
    }
  };

  const remove = async (passkey: PasskeySummary) => {
    if (!confirm(`移除“${passkey.label}”的通行密钥？恢复密码仍然可用。`)) return;
    setBusy(passkey.id);
    setError("");
    try {
      await api.removePasskey(passkey.id);
      setPasskeys((current) => current.filter((item) => item.id !== passkey.id));
    } catch (reason) {
      setError(friendlyError(reason));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="access-dialog-shell" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="access-dialog" role="dialog" aria-modal="true" aria-labelledby="access-title" onKeyDown={(event) => event.key === "Escape" && onClose()}>
        <header className="access-dialog-head">
          <div className="access-dialog-mark"><KeyRound size={18} /></div>
          <div><span>ACCESS CONTROL</span><h2 id="access-title">访问管理</h2></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="关闭访问管理"><X size={20} /></button>
        </header>

        <div className="access-dialog-body">
          <section className="access-intro">
            <ShieldCheck size={22} />
            <div><strong>通行密钥优先，恢复密码兜底。</strong><p>每台新设备可单独登记；若系统密码管理器会同步通行密钥，新设备会自动可用。</p></div>
          </section>

          <section className="access-register" aria-labelledby="register-title">
            <div className="access-section-title"><div><span>01</span><strong id="register-title">为这台设备添加通行密钥</strong></div><button type="button" onClick={() => void load()} aria-label="刷新注册状态"><RefreshCw size={14} /></button></div>
            {supported ? (
              <div className="access-register-row">
                <label><span>设备名称</span><input ref={labelRef} value={label} maxLength={60} onChange={(event) => setLabel(event.target.value)} placeholder="例如：OPPO Pad mini" /></label>
                <button type="button" className="access-primary-button" onClick={register} disabled={!label.trim() || !ceremony || Boolean(busy)}>
                  {busy === "registering" || busy === "loading" ? <LoaderCircle className="spin" size={18} /> : <Plus size={18} />}
                  添加
                </button>
              </div>
            ) : <p className="access-unsupported">当前浏览器不支持 WebAuthn，无法在这里创建通行密钥。</p>}
          </section>

          <section className="access-list" aria-labelledby="passkey-list-title">
            <div className="access-section-title"><div><span>02</span><strong id="passkey-list-title">已登记设备</strong></div><small>{passkeys.length}</small></div>
            {busy === "loading" && !passkeys.length ? (
              <div className="access-loading"><LoaderCircle className="spin" size={18} /> 正在读取</div>
            ) : passkeys.length ? passkeys.map((passkey) => (
              <article className="passkey-row" key={passkey.id}>
                <div className="passkey-icon"><Fingerprint size={20} /></div>
                <div className="passkey-copy"><strong>{passkey.label}</strong><span>最近使用：{displayDate(passkey.lastUsedAt)}</span></div>
                {passkey.synced && <small>可同步</small>}
                <button type="button" className="passkey-remove" onClick={() => void remove(passkey)} disabled={Boolean(busy)} aria-label={`移除 ${passkey.label}`}>
                  {busy === passkey.id ? <LoaderCircle className="spin" size={16} /> : <Trash2 size={16} />}
                </button>
              </article>
            )) : <div className="access-empty"><Fingerprint size={22} /><strong>还没有通行密钥。</strong><span>先在当前设备添加一枚；以后换设备时可继续追加。</span></div>}
          </section>

          {error && <p className="access-error" role="alert">{error}</p>}
          <p className="access-recovery"><KeyRound size={13} /> 原随机长密码继续保留，只作为新设备首次接入或应急恢复使用。</p>
        </div>
      </section>
    </div>
  );
}
